import drawCanvas from '@/components/modules/CanvasEditor/drawCanvas'
import { type Color } from '@backend/lib/db/typeRoots/color'
import { type ImageCache } from '@/components/modules/CanvasEditor/types'
import { type LayoutNode } from '@backend/lib/db/typeRoots/layoutNode'
import { type WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import { calculateCanvasSize } from './editor'
import { stampCanvasProvenance } from './canvasProvenance'

type RenderOptions = {
  nodes: LayoutNode[]
  aspectRatio: string
  backgroundColor?: Color
  /** When the canvas is AI-generated, the flattened PNG is stamped with the
   *  IPTC "composite contains AI" marker so platforms auto-disclose it (GH #118).
   *  Pass the canvas's provenance (`origin` / `hasSyntheticEdits`); omit for
   *  user-authored canvases. */
  aiProvenance?: Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'>
}

/**
 * Loads all images referenced by nodes into a cache
 */
async function loadImages(nodes: LayoutNode[]): Promise<ImageCache> {
  const imageNodes = nodes.filter((node) => node.data.type === 'image')

  const imagePromises = imageNodes.map(async (node) => {
    if (node.data.type !== 'image') return null

    const { source } = node.data
    if (source.type !== 'url' || !source.url) return null

    const img = new Image()
    img.crossOrigin = 'anonymous'
    // Cache-bust the crossOrigin request. If the same URL was loaded elsewhere as a
    // plain <img> (no crossOrigin) — e.g. a moodboard logo shown in the UI — the
    // browser caches an opaque, header-less response and reuses it here, so the CORS
    // load we need to export the canvas is blocked ("No Access-Control-Allow-Origin"
    // even though S3 serves it). A distinct URL sidesteps that poisoned entry. Cache
    // under the ORIGINAL url so drawCanvas lookups (keyed by node source.url) resolve.
    const loadUrl = source.url + (source.url.includes('?') ? '&' : '?') + 'canvasCors=1'
    img.src = loadUrl

    try {
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error(`Failed to load image: ${source.url}`))
      })
    } catch (err) {
      // One image failing to load must not sink the whole design — skip it and
      // render the rest (drawCanvas leaves that slot empty). Warn so it's visible.
      console.warn('[renderCanvasOffscreen] skipping image that failed to load', source.url, err)
      return null
    }

    return { cacheKey: source.url, img }
  })

  const loadedImages = await Promise.all(imagePromises)

  return loadedImages.reduce<ImageCache>((acc, item) => {
    if (item) {
      acc[item.cacheKey] = item.img
    }
    return acc
  }, {})
}

/**
 * Ensures fonts used by text nodes are loaded before drawing. Walks the
 * node-level options AND every content run, since a run can declare its
 * own family/weight/style/size (e.g. an inline italic accent inside a
 * sans-serif headline). Also walks legacy `preWrappedLines.runs` whose
 * style fields are still populated — pre-consolidation nodes carried
 * styling there instead of on `content`.
 */
async function loadFonts(nodes: LayoutNode[]): Promise<void> {
  const textNodes = nodes.filter((node) => node.data.type === 'text')
  if (textNodes.length === 0) return

  await document.fonts.ready

  const fontSpecs = new Set<string>()
  for (const node of textNodes) {
    if (node.data.type !== 'text') continue
    const { fontFamily, fontWeight, fontStyle, fontSize } = node.data.options
    fontSpecs.add(`${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`)
    for (const run of node.data.content) {
      fontSpecs.add(`${run.fontStyle} ${run.fontWeight} ${run.fontSize}px "${run.fontFamily}"`)
    }
    for (const line of node.data.options.preWrappedLines ?? []) {
      for (const run of line.runs ?? []) {
        if (run.fontFamily === undefined) continue
        fontSpecs.add(`${run.fontStyle} ${run.fontWeight} ${run.fontSize}px "${run.fontFamily}"`)
      }
    }
  }

  await Promise.all(Array.from(fontSpecs).map((spec) => document.fonts.load(spec)))
}

/**
 * Renders canvas nodes to a File without mounting to DOM
 */
export async function renderCanvasOffscreen({
  nodes,
  aspectRatio,
  backgroundColor = { r: 255, g: 255, b: 255, a: 255 },
  aiProvenance,
}: RenderOptions): Promise<File> {
  const { width, height } = calculateCanvasSize(aspectRatio)

  // Create offscreen canvas
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to get canvas context')
  }

  // Load all images and fonts in parallel
  const [imageCache] = await Promise.all([loadImages(nodes), loadFonts(nodes)])

  // Draw the canvas
  drawCanvas(backgroundColor, nodes, imageCache, ctx, width, height)

  // Convert to File
  const file = await new Promise<File>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(new File([blob], 'canvas.png', { type: 'image/png' }))
      } else {
        reject(new Error('Failed to create blob from canvas'))
      }
    }, 'image/png')
  })

  return aiProvenance ? stampCanvasProvenance(file, aiProvenance) : file
}
